import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { rupee } from "../lib/api";
import { useCart } from "../context/CartContext";
import { VegBadge } from "../components/VegBadge";
import { Minus, Plus, ArrowLeft, Star, Check } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [cust, setCust] = useState([]);
  const [addons, setAddons] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariant(r.data.variants?.length ? r.data.variants[0] : null);
      setCust([]); setAddons([]); setQty(1);
    });
  }, [slug]);

  const unit = variant ? variant.price : product?.base_price || 0;
  const extras = useMemo(() => [...cust, ...addons].reduce((s, o) => s + o.price, 0), [cust, addons]);
  const total = (unit + extras) * qty;

  const toggle = (list, setList, opt) => {
    setList(list.some((o) => o.name === opt.name) ? list.filter((o) => o.name !== opt.name) : [...list, opt]);
  };

  const buildEntry = () => ({
    product_id: product.id, slug: product.slug, name: product.name, image: product.image,
    variant: variant?.name || null, unit_price: unit, customizations: cust, add_ons: addons, quantity: qty, veg: product.veg,
  });

  const add = () => { addItem(buildEntry()); toast.success(`${product.name} added to cart`); };
  const buyNow = () => { addItem(buildEntry()); navigate("/checkout"); };

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy mb-5" data-testid="back-btn"><ArrowLeft size={16} /> Back to menu</button>
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="relative">
          <div className="absolute -inset-3 border border-gold/25 rounded-2xl hidden sm:block" />
          <img src={product.image} alt={product.name} className="relative rounded-2xl w-full aspect-[4/3] object-cover shadow-lg" />
          {product.best_seller && <span className="absolute top-4 left-4 flex items-center gap-1 bg-navy text-gold text-xs font-semibold px-3 py-1.5 rounded-full"><Star size={12} className="fill-gold" /> Best Seller</span>}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2"><VegBadge veg={product.veg} /><span className="overline text-gold">{product.subcategory || product.category}</span></div>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight">{product.name}</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">{product.description}</p>
          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground"><span>{product.serving_info}</span></div>

          <div className="mt-6 text-2xl font-semibold text-navy">{rupee(unit)}</div>

          {product.variants?.length > 0 && (
            <div className="mt-5" data-testid="variant-selector">
              <div className="text-sm font-semibold text-navy mb-2">Choose Size</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button key={v.name} onClick={() => setVariant(v)} data-testid={`variant-${v.name.replace(/\s/g, "-").toLowerCase()}`}
                    className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${variant?.name === v.name ? "border-gold bg-gold/10 text-navy" : "border-black/10 bg-white hover:border-gold"}`}>
                    {v.name} · {rupee(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.customizations?.length > 0 && (
            <div className="mt-6" data-testid="customizations">
              <div className="text-sm font-semibold text-navy mb-1">Customise Your Order</div>
              <p className="text-xs text-muted-foreground mb-3">Optional additions</p>
              <div className="space-y-2">
                {product.customizations.map((o) => {
                  const active = cust.some((c) => c.name === o.name);
                  return (
                    <button key={o.name} onClick={() => toggle(cust, setCust, o)} data-testid={`cust-${o.name.replace(/\s/g, "-").toLowerCase()}`}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm transition-colors ${active ? "border-gold bg-gold/5" : "border-black/10 bg-white hover:border-gold/50"}`}>
                      <span className="flex items-center gap-2.5"><span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? "bg-gold border-gold" : "border-black/25"}`}>{active && <Check size={11} className="text-navy" />}</span>{o.name}</span>
                      <span className="text-muted-foreground">{o.price > 0 ? `+ ${rupee(o.price)}` : "Free"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.add_ons?.length > 0 && (
            <div className="mt-6" data-testid="addons">
              <div className="text-sm font-semibold text-navy mb-3">Add-ons</div>
              <div className="space-y-2">
                {product.add_ons.map((o) => {
                  const active = addons.some((c) => c.name === o.name);
                  return (
                    <button key={o.name} onClick={() => toggle(addons, setAddons, o)} data-testid={`addon-${o.name.replace(/\s/g, "-").toLowerCase()}`}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm transition-colors ${active ? "border-gold bg-gold/5" : "border-black/10 bg-white hover:border-gold/50"}`}>
                      <span className="flex items-center gap-2.5"><span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? "bg-gold border-gold" : "border-black/25"}`}>{active && <Check size={11} className="text-navy" />}</span>{o.name}</span>
                      <span className="text-muted-foreground">+ {rupee(o.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border border-black/15 rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
            <div className="text-right ml-auto"><div className="text-xs text-muted-foreground">Total</div><div className="text-xl font-semibold text-navy" data-testid="live-total">{rupee(total)}</div></div>
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={add} data-testid="add-to-cart-btn" className="flex-1 btn-shine bg-navy text-cream font-semibold py-3.5 rounded-full hover:bg-navy-soft transition-colors">Add to Cart</button>
            <button onClick={buyNow} data-testid="buy-now-btn" className="flex-1 btn-shine bg-gold text-navy font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors">Buy Now</button>
          </div>
        </div>
      </div>
    </div>
  );
}
